/**
 * @fileoverview Rootally Scan SDK
 * Core functionality for facial landmark detection and analysis
 */

// Feedback messages for audio feedback
export const FEEDBACK_MESSAGES = {
  // Face Detection Messages
  NO_FACE: 'No face detected',
  
  // Position Adjustment Messages
  DISTANCE: 'Move your face closer to the camera',
  CENTER: 'Center your face in the frame',
  STRAIGHT: 'Keep your head straight',
  TURN_LEFT: 'Turn your head more to the left',
  TURN_RIGHT: 'Turn your head more to the right',
  TILT_DOWN: 'Tilt your head down more',
  
  // Eye-related Messages
  EYES_POSITION: 'Position your eyes within the guides',
  EYES_OPEN: 'Please open your eyes wider',
  
  // Success Messages
  READY: 'Ready to capture',
  POSITION_VALID: 'Position valid',
  EYES_VALID: 'Eyes valid',
  
  // Error Messages
  INVALID_POSITION: 'Invalid position type'
};

// Detect if device is mobile
export const isMobile = () => {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
};

// Camera and viewport constants
export const CAMERA_CONFIG = {
  WIDTH: window.innerWidth,
  HEIGHT: window.innerHeight,
  ASPECT_RATIO: isMobile() ? 16/9 : 4/3, // Use 16:9 for mobile, 4:3 for desktop
  FACING_MODE: 'user'
};

// Function to update camera dimensions
export const updateCameraDimensions = () => {
  const aspectRatio = CAMERA_CONFIG.ASPECT_RATIO;
  const windowRatio = window.innerWidth / window.innerHeight;

  console.log('windowRatio:', windowRatio);
  console.log('aspectRatio:', aspectRatio);
  if (windowRatio > aspectRatio) {
    // Window is wider than needed
    console.log('windowRatio > aspectRatio');
    CAMERA_CONFIG.HEIGHT = window.innerHeight;
    CAMERA_CONFIG.WIDTH = window.innerHeight * aspectRatio;
  } else {
    // Window is taller than needed
    console.log('windowRatio < aspectRatio');
    CAMERA_CONFIG.WIDTH = window.innerWidth;
    CAMERA_CONFIG.HEIGHT = window.innerWidth / aspectRatio;
  }
};

// Get optimal camera constraints based on device
export const getCameraConstraints = async () => {
  if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
    return {
      width: { ideal: CAMERA_CONFIG.WIDTH },
      height: { ideal: CAMERA_CONFIG.HEIGHT },
      facingMode: CAMERA_CONFIG.FACING_MODE
    };
  }

  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = devices.filter(device => device.kind === 'videoinput');
    
    // If we have access to detailed device info
    if (videoDevices.length > 0) {
      return {
        width: { ideal: CAMERA_CONFIG.WIDTH },
        height: { ideal: CAMERA_CONFIG.HEIGHT },
        facingMode: CAMERA_CONFIG.FACING_MODE,
        aspectRatio: { ideal: CAMERA_CONFIG.ASPECT_RATIO }
      };
    }
  } catch (error) {
    console.warn('Error getting camera capabilities:', error);
  }

  // Fallback to basic constraints
  return {
    width: { ideal: CAMERA_CONFIG.WIDTH },
    height: { ideal: CAMERA_CONFIG.HEIGHT },
    facingMode: CAMERA_CONFIG.FACING_MODE
  };
};

// Face detection constants
export const FACE_DETECTION_CONFIG = {
  MIN_FACE_SIZE_RATIO: 0.4,
  MAX_FACE_SIZE_RATIO: 0.8,
  MAX_TILT_ANGLE: 15,
  GUIDE_SCALE_X: 0.35,
  GUIDE_SCALE_Y: 0.45
};

// Eye detection constants
export const EYE_DETECTION_CONFIG = {
  ASPECT_RATIO_THRESHOLD: 0.25,
  OPEN_RATIO_THRESHOLD: 0.3,
  MIN_PADDING_SCALE: 0.05,
  MAX_PADDING_SCALE: 0.15,
  BASE_PADDING_RATIO: 0.2
};

// MediaPipe FaceMesh landmark indices
export const LANDMARK_INDICES = {
  // Core facial points
  NOSE_TIP: 1,
  LEFT_EYE_CENTER: 33,
  RIGHT_EYE_CENTER: 263,
  FACE_OVAL_LEFT: 234,
  FACE_OVAL_RIGHT: 454,

  // Left eye landmarks
  LEFT_EYE: {
    LANDMARKS: [33, 133, 157, 158, 159, 160, 161, 246],
    CONTOUR: [33, 133, 160, 144, 145, 153, 154, 155, 157, 158, 159, 160, 161, 163],
    ASPECT_RATIO_POINTS: [33, 159, 145, 133, 154, 153],
    OPENNESS_POINTS: [33, 133, 159, 145]
  },

  // Right eye landmarks
  RIGHT_EYE: {
    LANDMARKS: [362, 263, 386, 387, 388, 389, 390, 466],
    CONTOUR: [362, 263, 387, 373, 374, 380, 381, 382, 384, 385, 386, 387, 388, 390],
    ASPECT_RATIO_POINTS: [362, 386, 374, 263, 381, 380],
    OPENNESS_POINTS: [362, 263, 386, 374]
  }
};

// Basic geometric calculations for face and eye measurements

/**
 * Calculate Euclidean distance between two points in screen coordinates
 * @param {Object} point1 - First point with x,y coordinates (normalized)
 * @param {Object} point2 - Second point with x,y coordinates (normalized) 
 * @returns {number} Distance in pixels
 */
export const calculateDistance = (point1, point2) => {
  return Math.sqrt(
    Math.pow((point2.x - point1.x) * CAMERA_CONFIG.WIDTH, 2) +
    Math.pow((point2.y - point1.y) * CAMERA_CONFIG.HEIGHT, 2)
  );
};

/**
 * Calculate face scale based on eye distance
 * @param {Array} landmarks - Array of facial landmarks
 * @returns {number} Face scale value based on eye distance
 */
export const calculateFaceScale = (landmarks) => {
  const leftEye = landmarks[LANDMARK_INDICES.LEFT_EYE_CENTER];
  const rightEye = landmarks[LANDMARK_INDICES.RIGHT_EYE_CENTER];
  const eyeDistance = Math.sqrt(
    Math.pow((rightEye.x - leftEye.x) * CAMERA_CONFIG.WIDTH, 2) +
    Math.pow((rightEye.y - leftEye.y) * CAMERA_CONFIG.HEIGHT, 2)
  );
  return eyeDistance;
};

/**
 * Calculate center point of an eye from its landmarks
 * @param {Array} landmarks - Array of facial landmarks
 * @param {Array} eyeLandmarks - Array of landmark indices for the eye
 * @returns {Object} Center point coordinates {x, y}
 */
export const calculateEyeCenter = (landmarks, eyeLandmarks) => {
  let sumX = 0, sumY = 0;
  eyeLandmarks.forEach(index => {
    sumX += landmarks[index].x * CAMERA_CONFIG.WIDTH;
    sumY += landmarks[index].y * CAMERA_CONFIG.HEIGHT;
  });
  return {
    x: sumX / eyeLandmarks.length,
    y: sumY / eyeLandmarks.length
  };
};

/**
 * Check if a point is within the guide ellipse
 * @param {number} x - X coordinate in pixels
 * @param {number} y - Y coordinate in pixels
 * @returns {boolean} True if point is within guide
 */
export const isPointWithinGuide = (x, y) => {
  const centerX = CAMERA_CONFIG.WIDTH / 2;
  const centerY = CAMERA_CONFIG.HEIGHT / 2;
  const radiusX = CAMERA_CONFIG.WIDTH * FACE_DETECTION_CONFIG.GUIDE_SCALE_X;
  const radiusY = CAMERA_CONFIG.HEIGHT * FACE_DETECTION_CONFIG.GUIDE_SCALE_Y;
  
  const normalizedX = (x - centerX) / radiusX;
  const normalizedY = (y - centerY) / radiusY;
  
  return (Math.pow(normalizedX, 2) + Math.pow(normalizedY, 2)) <= 1;
};

// Face position and orientation functions

/**
 * Check if face is properly positioned within the guide
 * @param {Array} landmarks - Array of facial landmarks
 * @returns {boolean} True if face is within guide boundaries
 */
export const isFaceWithinGuide = (landmarks) => {
  const centerX = CAMERA_CONFIG.WIDTH / 2;
  const centerY = CAMERA_CONFIG.HEIGHT / 2;
  const radiusX = CAMERA_CONFIG.WIDTH * FACE_DETECTION_CONFIG.GUIDE_SCALE_X;
  const radiusY = CAMERA_CONFIG.HEIGHT * FACE_DETECTION_CONFIG.GUIDE_SCALE_Y;
  
  const nose = landmarks[LANDMARK_INDICES.NOSE_TIP];
  const noseX = nose.x * CAMERA_CONFIG.WIDTH;
  const noseY = nose.y * CAMERA_CONFIG.HEIGHT;
  
  const normalizedX = (noseX - centerX) / radiusX;
  const normalizedY = (noseY - centerY) / radiusY;
  
  return (Math.pow(normalizedX, 2) + Math.pow(normalizedY, 2)) <= 1.1;
};

/**
 * Calculate face tilt angle in degrees
 * @param {Array} landmarks - Array of facial landmarks
 * @returns {number} Angle of tilt in degrees
 */
export const calculateFaceTilt = (landmarks) => {
  const leftEye = landmarks[LANDMARK_INDICES.LEFT_EYE_CENTER];
  const rightEye = landmarks[LANDMARK_INDICES.RIGHT_EYE_CENTER];
  
  const deltaY = (rightEye.y - leftEye.y) * CAMERA_CONFIG.HEIGHT;
  const deltaX = (rightEye.x - leftEye.x) * CAMERA_CONFIG.WIDTH;
  const angleInDegrees = Math.abs(Math.atan2(deltaY, deltaX) * (180 / Math.PI));
  
  return angleInDegrees;
};

/**
 * Check if both eyes are within the guide area
 * @param {Array} landmarks - Array of facial landmarks
 * @returns {boolean} True if both eyes are within guide
 */
export const areEyesWithinGuide = (landmarks) => {
  const leftEyeCenter = calculateEyeCenter(landmarks, LANDMARK_INDICES.LEFT_EYE.LANDMARKS);
  const rightEyeCenter = calculateEyeCenter(landmarks, LANDMARK_INDICES.RIGHT_EYE.LANDMARKS);
  
  return isPointWithinGuide(leftEyeCenter.x, leftEyeCenter.y) && 
         isPointWithinGuide(rightEyeCenter.x, rightEyeCenter.y);
};

/**
 * Check if face size is within acceptable range
 * @param {Array} landmarks - Array of facial landmarks
 * @returns {boolean} True if face size is within acceptable range
 */
export const isFaceSizeValid = (landmarks) => {
  const faceWidth = Math.abs(
    landmarks[LANDMARK_INDICES.FACE_OVAL_LEFT].x - 
    landmarks[LANDMARK_INDICES.FACE_OVAL_RIGHT].x
  );
  return faceWidth > FACE_DETECTION_CONFIG.MIN_FACE_SIZE_RATIO && 
         faceWidth < FACE_DETECTION_CONFIG.MAX_FACE_SIZE_RATIO;
};

// Eye-specific calculations

/**
 * Calculate Eye Aspect Ratio (EAR) for blink detection
 * EAR = (P2-P6 + P3-P5) / (2 * P1-P4)
 * @param {Array} landmarks - Array of facial landmarks
 * @param {Array} eyeContour - Array of 6 points forming the eye contour
 * @returns {number} Eye aspect ratio value
 */
export const calculateEyeAspectRatio = (landmarks, eyeContour) => {
  const p2_p6 = calculateDistance(landmarks[eyeContour[1]], landmarks[eyeContour[5]]);
  const p3_p5 = calculateDistance(landmarks[eyeContour[2]], landmarks[eyeContour[4]]);
  const p1_p4 = calculateDistance(landmarks[eyeContour[0]], landmarks[eyeContour[3]]);
  
  return (p2_p6 + p3_p5) / (2.0 * p1_p4);
};

/**
 * Calculate ratio of eye vertical opening to horizontal width
 * @param {Array} landmarks - Array of facial landmarks
 * @param {Array} eyePoints - Array of [left, right, top, bottom] point indices
 * @returns {number} Eye openness ratio
 */
export const calculateEyeOpenRatio = (landmarks, eyePoints) => {
  const leftPoint = landmarks[eyePoints[0]];
  const rightPoint = landmarks[eyePoints[1]];
  const upperPoint = landmarks[eyePoints[2]];
  const lowerPoint = landmarks[eyePoints[3]];
  
  const horizontalDist = calculateDistance(leftPoint, rightPoint);
  const verticalDist = calculateDistance(upperPoint, lowerPoint);
  
  return verticalDist / horizontalDist;
};

/**
 * Check if both eyes are fully open based on multiple metrics
 * @param {Array} landmarks - Array of facial landmarks
 * @returns {boolean} True if both eyes are fully open
 */
export const areEyesFullyOpen = (landmarks) => {
  // Left eye points for aspect ratio
  const leftEAR = calculateEyeAspectRatio(landmarks, LANDMARK_INDICES.LEFT_EYE.ASPECT_RATIO_POINTS);
  // Right eye points for aspect ratio
  const rightEAR = calculateEyeAspectRatio(landmarks, LANDMARK_INDICES.RIGHT_EYE.ASPECT_RATIO_POINTS);
  
  // Additional check using vertical/horizontal ratio
  const leftEyeRatio = calculateEyeOpenRatio(landmarks, LANDMARK_INDICES.LEFT_EYE.OPENNESS_POINTS);
  const rightEyeRatio = calculateEyeOpenRatio(landmarks, LANDMARK_INDICES.RIGHT_EYE.OPENNESS_POINTS);
  
  const leftEyeOpen = leftEyeRatio > EYE_DETECTION_CONFIG.OPEN_RATIO_THRESHOLD;
  const rightEyeOpen = rightEyeRatio > EYE_DETECTION_CONFIG.OPEN_RATIO_THRESHOLD;
  
  return leftEyeOpen && rightEyeOpen && 
         leftEAR > EYE_DETECTION_CONFIG.ASPECT_RATIO_THRESHOLD && 
         rightEAR > EYE_DETECTION_CONFIG.ASPECT_RATIO_THRESHOLD;
};

/**
 * Calculate dynamic padding for eye region extraction based on face scale
 * @param {number} width - Width of eye region
 * @param {number} height - Height of eye region
 * @param {number} faceScale - Current face scale value
 * @returns {number} Padding value in pixels
 */
export const calculateDynamicPadding = (width, height, faceScale) => {
  const basePadding = Math.max(width, height) * 0.2;
  const minPadding = faceScale * 0.05;
  const maxPadding = faceScale * 0.15;
  return Math.min(Math.max(basePadding, minPadding), maxPadding);
};

/**
 * Calculate bounding box for eye region extraction
 * @param {Array} landmarks - Array of facial landmarks
 * @param {Array} eyeContour - Array of landmark indices forming eye contour
 * @param {number} faceScale - Current face scale value
 * @returns {Object} Bounding box coordinates {minX, minY, maxX, maxY, width, height}
 */
export const calculateEyeBoundingBox = (landmarks, eyeContour, faceScale) => {
  let minX = Infinity, minY = Infinity;
  let maxX = -Infinity, maxY = -Infinity;

  eyeContour.forEach(index => {
    const point = landmarks[index];
    const x = point.x * CAMERA_CONFIG.WIDTH;
    const y = point.y * CAMERA_CONFIG.HEIGHT;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  });

  const width = maxX - minX;
  const height = maxY - minY;
  const padding = calculateDynamicPadding(width, height, faceScale);

  return {
    minX: Math.max(0, minX - padding),
    minY: Math.max(0, minY - padding),
    maxX: Math.min(CAMERA_CONFIG.WIDTH, maxX + padding),
    maxY: Math.min(CAMERA_CONFIG.HEIGHT, maxY + padding),
    width: maxX - minX + 2 * padding,
    height: maxY - minY + 2 * padding
  };
};

export const extractEyeRegion = (landmarks, eye) => {
  const eyeContour = eye === 'left' ? 
    LANDMARK_INDICES.LEFT_EYE.CONTOUR : 
    LANDMARK_INDICES.RIGHT_EYE.CONTOUR;

    let minX = Infinity, minY = Infinity;
    let maxX = -Infinity, maxY = -Infinity;

  // Find bounding box of eye region
  eyeContour.forEach(index => {
    const point = landmarks[index];
    const x = point.x * 640;
    const y = point.y * 480;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  });

  const objWidth = maxX - minX;
  const objHeight = maxY - minY;
  const padding = calculateDynamicPadding(objWidth, objHeight, calculateFaceScale(landmarks));

  minX = Math.max(0, minX - padding);
  maxX = Math.min(640, maxX + padding);
  minY = Math.max(0, minY - padding);
  maxY = Math.min(480, maxY + padding);

  const width = maxX - minX;
  const height = maxY - minY;
  
  // Calculate padding based on eye size
  //const padding = width * 0.2; // 20% padding

  // Return normalized coordinates (0-1 range)
  return {
    x: minX,
    y: minY,
    width: width,
    height: height
  };
};
export const getLandmarkIndices = (region) => {
  switch (region) {
    case 'left_eye':
      return LANDMARK_INDICES.LEFT_EYE.CONTOUR;
    case 'right_eye':
      return LANDMARK_INDICES.RIGHT_EYE.CONTOUR;
    default:
      return [];
  }
};
export const extractRegion = (landmarks, region) => {

  const landmarkIndices = getLandmarkIndices(region);
      let minX = Infinity, minY = Infinity;
      let maxX = -Infinity, maxY = -Infinity;
  
    // Find bounding box of eye region
    landmarkIndices.forEach(index => {
      const point = landmarks[index];
      const x = point.x * 640;
      const y = point.y * 480;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    });
  
    const objWidth = maxX - minX;
    const objHeight = maxY - minY;
    const padding = calculateDynamicPadding(objWidth, objHeight, calculateFaceScale(landmarks));
  
    minX = Math.max(0, minX - padding);
    maxX = Math.min(640, maxX + padding);
    minY = Math.max(0, minY - padding);
    maxY = Math.min(480, maxY + padding);
  
    const width = maxX - minX;
    const height = maxY - minY;
    
    // Calculate padding based on eye size
    //const padding = width * 0.2; // 20% padding
  
    // Return normalized coordinates (0-1 range)
    return {
      x: minX,
      y: minY,
      width: width,
      height: height
    };
  };

/**
 * Check if face is turned left enough
 * @param {Array} landmarks - Array of facial landmarks
 * @returns {Object} Validation result {isValid, feedback}
 */
const checkLeftTurn = (landmarks) => {
  const nose = landmarks[LANDMARK_INDICES.NOSE_TIP];
  const leftEye = landmarks[LANDMARK_INDICES.LEFT_EYE_CENTER];
  const rightEye = landmarks[LANDMARK_INDICES.RIGHT_EYE_CENTER];
  
  const centerX = (leftEye.x + rightEye.x) / 2;
  const turnAmount = (nose.x - centerX) * CAMERA_CONFIG.WIDTH;
  
  if (turnAmount > -50) {
    console.log('turnAmount', turnAmount);
    return {
      isValid: false,
      feedback: {
        key: 'TURN_LEFT',
        message: FEEDBACK_MESSAGES.TURN_LEFT
      }
    };
  }
  
  return {
    isValid: true,
    feedback: {
      key: 'POSITION_VALID',
      message: FEEDBACK_MESSAGES.POSITION_VALID
    }
  };
};

/**
 * Check if face is turned right enough
 * @param {Array} landmarks - Array of facial landmarks
 * @returns {Object} Validation result {isValid, feedback}
 */
const checkRightTurn = (landmarks) => {
  const nose = landmarks[LANDMARK_INDICES.NOSE_TIP];
  const leftEye = landmarks[LANDMARK_INDICES.LEFT_EYE_CENTER];
  const rightEye = landmarks[LANDMARK_INDICES.RIGHT_EYE_CENTER];
  
  const centerX = (leftEye.x + rightEye.x) / 2;
  const turnAmount = (nose.x - centerX) * CAMERA_CONFIG.WIDTH;
  
  if (turnAmount < 50) {
    return {
      isValid: false,
      feedback: {
        key: 'TURN_RIGHT',
        message: FEEDBACK_MESSAGES.TURN_RIGHT
      }
    };
  }
  
  return {
    isValid: true,
    feedback: {
      key: 'POSITION_VALID',
      message: FEEDBACK_MESSAGES.POSITION_VALID
    }
  };
};

/**
 * Check if head is tilted down enough
 * @param {Array} landmarks - Array of facial landmarks
 * @returns {Object} Validation result {isValid, feedback}
 */
const checkDownTilt = (landmarks) => {
  const nose = landmarks[LANDMARK_INDICES.NOSE_TIP];
  const leftEye = landmarks[LANDMARK_INDICES.LEFT_EYE_CENTER];
  const rightEye = landmarks[LANDMARK_INDICES.RIGHT_EYE_CENTER];
  
  const eyeY = (leftEye.y + rightEye.y) / 2;
  const tiltAmount = (nose.y - eyeY) * CAMERA_CONFIG.HEIGHT;
  
  if (tiltAmount < 30) {
    return {
      isValid: false,
      feedback: {
        key: 'TILT_DOWN',
        message: FEEDBACK_MESSAGES.TILT_DOWN
      }
    };
  }
  
  return {
    isValid: true,
    feedback: {
      key: 'POSITION_VALID',
      message: FEEDBACK_MESSAGES.POSITION_VALID
    }
  };
};

/**
 * Validates face based on position type
 * @param {Array} landmarks - Array of facial landmarks
 * @param {string} position - Position type ('front', 'left', 'right', 'down')
 * @returns {Object} Validation result {isValid, feedback}
 */
const validateFacePosition = (landmarks, position) => {
  if (!isFaceSizeValid(landmarks)) {
    return {
      isValid: false,
      feedback: {
        key: 'DISTANCE',
        message: FEEDBACK_MESSAGES.DISTANCE
      }
    };
  }

  switch(position) {
    case 'front':
      if (!isFaceWithinGuide(landmarks)) {
        return {
          isValid: false,
          feedback: {
            key: 'CENTER',
            message: FEEDBACK_MESSAGES.CENTER
          }
        };
      }
      if (calculateFaceTilt(landmarks) > FACE_DETECTION_CONFIG.MAX_TILT_ANGLE) {
        return {
          isValid: false,
          feedback: {
            key: 'STRAIGHT',
            message: FEEDBACK_MESSAGES.STRAIGHT
          }
        };
      }
      break;

    case 'left':
      return checkLeftTurn(landmarks);

    case 'right':
      return checkRightTurn(landmarks);

    case 'down':
      return checkDownTilt(landmarks);

    default:
      return {
        isValid: false,
        feedback: {
          key: 'INVALID_POSITION',
          message: FEEDBACK_MESSAGES.INVALID_POSITION
        }
      };
  }

  return {
    isValid: true,
    feedback: {
      key: 'POSITION_VALID',
      message: FEEDBACK_MESSAGES.POSITION_VALID
    }
  };
};

/**
 * Validates eye region if required
 * @param {Array} landmarks - Array of facial landmarks
 * @param {string} extractRegion - Region to extract ('left_eye', 'right_eye', or null)
 * @returns {Object} Validation result {isValid, feedback}
 */
const validateEyeRegion = (landmarks, extractRegion) => {
  if (!extractRegion || !extractRegion.includes('eye')) {
    return {
      isValid: true,
      feedback: {
        key: 'EYES_VALID',
        message: FEEDBACK_MESSAGES.EYES_VALID
      }
    };
  }

  if (!areEyesWithinGuide(landmarks)) {
    return {
      isValid: false,
      feedback: {
        key: 'EYES_POSITION',
        message: FEEDBACK_MESSAGES.EYES_POSITION
      }
    };
  }

  if (!areEyesFullyOpen(landmarks)) {
    return {
      isValid: false,
      feedback: {
        key: 'EYES_OPEN',
        message: FEEDBACK_MESSAGES.EYES_OPEN
      }
    };
  }

  return {
    isValid: true,
    feedback: {
      key: 'EYES_VALID',
      message: FEEDBACK_MESSAGES.EYES_VALID
    }
  };
};

/**
 * Get region coordinates based on configuration
 * @param {Array} landmarks - Array of facial landmarks
 * @param {string} extractRegion - Region to extract ('left_eye', 'right_eye', or null)
 * @returns {Object} Region coordinates
 */
const getRegionCoordinates = (landmarks, region) => {
  if (!region) {
    // Return full face region
    return {    
      x: 0,
      y: 0,
      width: CAMERA_CONFIG.WIDTH,
      height: CAMERA_CONFIG.HEIGHT
    };
  }

  // Extract specific region (eye)
  return extractRegion(landmarks, region);
};

// Main process function with decoupled validations
export const process = (results, config = {}) => {
  if (!results.multiFaceLandmarks?.length) {
    return {
      isValid: false,
      feedback: {
        key: 'NO_FACE',
        message: FEEDBACK_MESSAGES.NO_FACE
      },
      region: null
    };
  }

  const landmarks = results.multiFaceLandmarks[0];
  const position = config.position || 'front';
  const extractRegion = config.extractRegion || null;

  // Step 1: Validate face position
  const faceValidation = validateFacePosition(landmarks, position);
  if (!faceValidation.isValid) {
    return {
      isValid: false,
      feedback: faceValidation.feedback,
      region: null
    };
  }

  // Step 2: Validate eye region if required
  const eyeValidation = validateEyeRegion(landmarks, extractRegion);
  if (!eyeValidation.isValid) {
    return {
      isValid: false,
      feedback: eyeValidation.feedback,
      region: null
    };
  }

  // Step 3: Get region coordinates
  const region = getRegionCoordinates(landmarks, extractRegion);

  // All validations passed
  return {
    isValid: true,
    feedback: {
      key: 'READY',
      message: FEEDBACK_MESSAGES.READY
    },
    region: region,
    eyeCenters: {
      left: calculateEyeCenter(landmarks, LANDMARK_INDICES.LEFT_EYE.LANDMARKS),
      right: calculateEyeCenter(landmarks, LANDMARK_INDICES.RIGHT_EYE.LANDMARKS)
    }
  };
}; 